

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Medium, UserLocation } from '../types';
import { ALL_MEDIUMS } from '../data/mediums';
import { StarIcon, ShieldIcon, PhoneIcon, TrendingUpIcon, MoonIcon, GiftIcon, CalendarIcon, AwardIcon, UserIcon, XIcon } from './Icons';

interface LiveActivityFeedProps {
    userLocation?: UserLocation;
    onProfileClick: (medium: Medium) => void;
    onOpenPremium: () => void;
}

// LISTE ÉTENDUE DE PRÉNOMS POUR ÉVITER LES RÉPÉTITIONS
const CLIENT_NAMES = [
    "Sophie", "Marc", "Julie", "Thomas", "Claire", "Nicolas", "Aurélie", "Jean", "Marie", "Lucas", 
    "Sarah", "David", "Elodie", "Kevin", "Sandrine", "Béatrice", "Dominique", "Françoise", "Martine", 
    "Monique", "Camille", "Léa", "Manon", "Chloe", "Océane", "Inès", "Justine", "Clara", "Morgane", 
    "Elsa", "Jade", "Louise", "Alice", "Zoe", "Eva", "Lisa", "Charlotte", "Amandine", "Audrey", 
    "Noémie", "Marion", "Mathilde", "Anaïs", "Lucie", "Emilie", "Florence", "Hélène", "Michel", 
    "Philippe", "Alain", "Christian", "Jacques", "Stéphane", "Patrick", "Antoine", "Pierre", "Raphaël", 
    "Gabriel", "Julien", "Laurent", "Eric", "Olivier", "Thierry", "Pascal", "Christophe", "Frédéric", 
    "Maxime", "Alexandre", "Romain", "Anthony", "Adrien", "Bastien", "Vincent", "Hugo", "Louis", 
    "Arthur", "Leo", "Nathan", "Paul", "Enzo", "Mathieu", "Guillaume", "Sébastien", "Florian", 
    "Clément", "Benjamin", "Damien", "Arnaud", "Xavier", "Bertrand", "Mélanie", "Cécile", "Laetitia", 
    "Virginie", "Caroline", "Stéphanie", "Corinne", "Christine", "Catherine", "Nathalie", "Isabelle", 
    "Valérie", "Sylvie", "Véronique", "Chantal", "Joëlle", "Danielle", "Michèle", "Nicole", "Anne", 
    "Brigitte", "Elisabeth", "Monique", "Thérèse", "Christiane", "Annie", "Jacqueline", "Simone", 
    "Odette", "Ginette", "Madeleine", "Jeanne", "Suzanne", "Marguerite", "Yvonne", "Germaine", 
    "Paulette", "Renée", "Denise", "Georgette", "Andrée", "Henriette", "Raymonde", "Lucienne", 
    "Gilberte", "Marcelle", "Fernande", "Geneviève", "Liliane", "Huguette", "Arlette", "Colette",
    "Emma", "Liam", "Noah", "Ethan", "Mael", "Jules", "Adam", "Sacha", "Isaac", "Noé"
];

const CITIES = ["Paris", "Lyon", "Marseille", "Bruxelles", "Genève", "Bordeaux", "Lille", "Nice", "Toulouse", "Nantes", "Strasbourg", "Montpellier", "Rennes", "Reims", "Toulon", "Grenoble", "Angers", "Dijon", "Brest", "Le Mans"];

// Les 5 gagnants fixes du jour pour la cohérence
const DAILY_WINNERS = ["Chantal D.", "Patrick M.", "Sabrina K.", "Jean-Luc B.", "Audrey T."];

const LiveActivityFeed: React.FC<LiveActivityFeedProps> = ({ userLocation, onProfileClick, onOpenPremium }) => {
    // --- STATE ---
    const [consultationCount, setConsultationCount] = useState(0);
    const [currentActivity, setCurrentActivity] = useState<React.ReactNode | null>(null);
    const [animationKey, setAnimationKey] = useState(0); // Force re-render for animation
    const [isNightMode, setIsNightMode] = useState(false);
    
    // --- SCROLL VISIBILITY STATE ---
    const [isVisible, setIsVisible] = useState(true);
    const lastScrollY = useRef(0);

    // --- MEMOIRE TAMPON (ANTI-REPETITION) ---
    // On stocke les TEXTES et non les ID pour garantir l'unicité du contenu visible
    const shownReviewTexts = useRef<Set<string>>(new Set());
    const shownClientNames = useRef<Set<string>>(new Set());

    // --- PAUSE AUTOMATIQUE POUR LES VRAIS AVIS ---
    const isPausedRef = useRef(false);

    // --- PRÉPARATION DES DONNÉES ---
    const allSiteReviews = useMemo(() => {
        return ALL_MEDIUMS.flatMap(m => 
            m.reviewsList
                .filter(r => r.text && r.text.length > 10 && r.text.length < 120) 
                .map(r => ({
                    ...r,
                    mediumName: m.name,
                    mediumImg: m.imageUrl,
                    mediumObj: m
                }))
        );
    }, []);

    // --- HELPERS UNICITÉ ---
    const getUniqueRandomName = () => {
        const available = CLIENT_NAMES.filter(n => !shownClientNames.current.has(n));
        if (available.length === 0) {
            shownClientNames.current.clear();
            return CLIENT_NAMES[Math.floor(Math.random() * CLIENT_NAMES.length)];
        }
        const selected = available[Math.floor(Math.random() * available.length)];
        shownClientNames.current.add(selected);
        return selected;
    };

    const getUniqueRandomReview = () => {
        // Filtrer par TEXTE pour éviter que la même phrase revienne
        const available = allSiteReviews.filter(r => !shownReviewTexts.current.has(r.text));
        
        if (available.length === 0) {
            shownReviewTexts.current.clear();
            return allSiteReviews[Math.floor(Math.random() * allSiteReviews.length)];
        }
        const selected = available[Math.floor(Math.random() * available.length)];
        shownReviewTexts.current.add(selected.text);
        return selected;
    };

    // --- EVENT LISTENER: VRAIS AVIS INSTANTANÉS (PRIORITAIRE) ---
    useEffect(() => {
        const handleRealRating = (e: any) => {
            const { mediumId, author, rating, text } = e.detail;
            const medium = ALL_MEDIUMS.find(m => m.id === mediumId);
            
            if (medium) {
                // 1. Mettre en PAUSE le cycle automatique pour que ce message reste affiché
                isPausedRef.current = true;

                // 2. Créer l'élément visuel "Vrai Avis" (Design Premium)
                const ratingNotification = (
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => onProfileClick(medium)}>
                        <div className="relative shrink-0">
                            <img src={medium.imageUrl} className="w-10 h-10 rounded-full border-2 border-gold-400 object-cover shadow-[0_0_15px_#fbbf24] bg-black" alt={medium.name} />
                            <div className="absolute -bottom-1 -right-1 bg-gradient-to-r from-gold-500 to-yellow-300 text-black text-[9px] font-bold px-1.5 rounded-full border border-black shadow-sm animate-bounce">
                                {rating}/5
                            </div>
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] text-gold-400 font-bold uppercase mb-0.5 animate-pulse flex items-center gap-1">
                                <StarIcon className="w-3 h-3 fill-current" /> NOUVEL AVIS CLIENT
                            </p>
                            <p className="text-white text-[10px] leading-tight truncate max-w-[220px]">
                                <span className="font-bold text-gold-200">{author}</span> a donné {rating}/5 à {medium.name}
                            </p>
                            {text && (
                                <p className="text-gray-300 text-[9px] italic mt-0.5 truncate max-w-[220px]">
                                    "{text}"
                                </p>
                            )}
                        </div>
                    </div>
                );
                
                // 3. Forcer l'affichage
                setCurrentActivity(ratingNotification);
                setAnimationKey(prev => prev + 1);

                // 4. Relancer le cycle après 10 secondes (Laisser le temps de lire)
                setTimeout(() => {
                    isPausedRef.current = false;
                }, 10000);
            }
        };

        window.addEventListener('astro_new_rating', handleRealRating);
        return () => window.removeEventListener('astro_new_rating', handleRealRating);
    }, [onProfileClick]);

    // --- LOGIQUE SCROLL MOBILE (SMART HIDE) ---
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            if (Math.abs(currentScrollY - lastScrollY.current) < 10) return;

            if (currentScrollY < 50) {
                setIsVisible(true);
            } else if (currentScrollY > lastScrollY.current) {
                setIsVisible(false);
            } else {
                setIsVisible(true);
            }
            lastScrollY.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // --- LOGIQUE CONSULTATIONS STRICTE (JOUR/NUIT) ---
    useEffect(() => {
        const updateTimeAndCount = () => {
            const hour = new Date().getHours();
            const isNight = hour >= 23 || hour < 8;
            setIsNightMode(isNight);

            if (isNight) {
                setConsultationCount(0);
                return;
            }

            let min = 8, max = 20; 
            if (hour >= 19) { min = 18; max = 32; } 
            
            setConsultationCount(Math.floor(Math.random() * (max - min + 1)) + min);
        };

        updateTimeAndCount();
        const interval = setInterval(updateTimeAndCount, 10000);
        return () => clearInterval(interval);
    }, []);

    // --- GÉNÉRATEURS D'ÉVÉNEMENTS ---
    
    const renderReview = () => {
        const review = getUniqueRandomReview();
        if (!review) return null;
        const firstName = review.author.split(' ')[0];
        if (firstName) shownClientNames.current.add(firstName);

        return (
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => onProfileClick(review.mediumObj)}>
                <div className="relative shrink-0">
                    <img src={review.mediumImg} className="w-8 h-8 rounded-full border border-gold-400 object-cover" alt={review.mediumName} />
                    <div className="absolute -bottom-1 -right-1 bg-yellow-500 text-black text-[7px] font-bold px-1 rounded-full">5/5</div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-gold-400 font-bold uppercase mb-0.5">Nouvel Avis Vérifié</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[180px]">
                        <span className="font-bold">{review.author}</span> : "{review.text}"
                    </p>
                </div>
            </div>
        );
    };

    const renderConsultation = () => {
        const activeMediums = ALL_MEDIUMS.filter(m => m.status !== 'away');
        const randomMedium = activeMediums[Math.floor(Math.random() * activeMediums.length)] || ALL_MEDIUMS[0];
        const randomCity = CITIES[Math.floor(Math.random() * CITIES.length)];
        
        return (
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => onProfileClick(randomMedium)}>
                <div className="relative shrink-0">
                    <div className="w-8 h-8 rounded-full bg-indigo-900/50 border border-indigo-500 flex items-center justify-center">
                        <PhoneIcon className="w-4 h-4 text-indigo-300 animate-pulse" />
                    </div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-indigo-300 font-bold uppercase mb-0.5">Consultation en cours</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[180px]">
                        Un client de <span className="font-bold">{randomCity}</span> consulte {randomMedium.name}.
                    </p>
                </div>
            </div>
        );
    };

    const renderMediumLogin = () => {
        const activeMediums = ALL_MEDIUMS.filter(m => m.status === 'available');
        const randomMedium = activeMediums[Math.floor(Math.random() * activeMediums.length)] || ALL_MEDIUMS[0];
        
        return (
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => onProfileClick(randomMedium)}>
                <div className="relative shrink-0">
                    <img src={randomMedium.imageUrl} className="w-8 h-8 rounded-full border-2 border-green-500 object-cover shadow-[0_0_10px_#22c55e]" alt={randomMedium.name} />
                    <span className="absolute top-0 right-0 flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-green-400 font-bold uppercase mb-0.5">Vient de se connecter</p>
                    <p className="text-white text-[10px] leading-tight group-hover:text-gold-400 transition-colors truncate max-w-[180px]">
                        <span className="font-bold text-xs">{randomMedium.name}</span> est disponible !
                    </p>
                </div>
            </div>
        );
    };

    const renderWinner = () => {
        const randomClient = getUniqueRandomName(); 
        return (
            <div className="flex items-center gap-3 cursor-pointer" onClick={onOpenPremium}>
                <div className="relative shrink-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-600 to-yellow-400 flex items-center justify-center shadow-lg">
                        <GiftIcon className="w-4 h-4 text-amber-300" />
                    </div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-yellow-300 font-bold uppercase mb-0.5 animate-pulse">Félicitations !</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[180px]">
                        <span className="font-bold">{randomClient}</span> a gagné 10 min gratuites.
                    </p>
                </div>
            </div>
        );
    };

    const renderTopRatedNight = () => {
        const topMediums = ALL_MEDIUMS.filter(m => m.rating >= 4.8);
        const randomMedium = topMediums[Math.floor(Math.random() * topMediums.length)];
        
        return (
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => onProfileClick(randomMedium)}>
                <div className="relative shrink-0">
                    <img src={randomMedium.imageUrl} className="w-8 h-8 rounded-full border-2 border-gold-500 object-cover" alt={randomMedium.name} />
                    <div className="absolute -bottom-1 -right-1 bg-gold-500 text-black text-[8px] font-bold px-1.5 rounded-full border border-black">TOP</div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-gold-400 font-bold uppercase mb-0.5">Expert du Jour (5/5)</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[280px]">
                        Découvrez <span className="font-bold">{randomMedium.name}</span>. Plus de {randomMedium.reviews} avis positifs.
                    </p>
                </div>
            </div>
        );
    };

    const renderNightWinner = () => {
        const winner = DAILY_WINNERS[Math.floor(Math.random() * DAILY_WINNERS.length)];
        return (
            <div className="flex items-center gap-3" onClick={onOpenPremium}>
                <div className="relative shrink-0">
                    <div className="w-8 h-8 rounded-full bg-purple-900 border border-gold-500 flex items-center justify-center">
                        <AwardIcon className="w-4 h-4 text-gold-400" />
                    </div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-purple-300 font-bold uppercase mb-0.5">Gagnants du Jour</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[280px]">
                        Bravo à <span className="font-bold text-gold-400">{winner}</span> pour son gain de 10 min !
                    </p>
                </div>
            </div>
        );
    };

    const renderNightWelcome = () => {
        const randomClient = getUniqueRandomName();
        return (
            <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-900/50 border border-blue-400 flex items-center justify-center">
                        <UserIcon className="w-4 h-4 text-purple-300" />
                    </div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-blue-300 font-bold uppercase mb-0.5">Bienvenue</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[280px]">
                        Bienvenue à <span className="font-bold">{randomClient}</span>, nouvel inscrit sur AstroX.
                    </p>
                </div>
            </div>
        );
    };

    const renderNightStats = () => {
        const count = 45 + Math.floor(Math.random() * 30);
        return (
            <div className="flex items-center gap-3">
                <div className="relative shrink-0">
                    <div className="w-8 h-8 rounded-full bg-green-900/30 border border-green-500 flex items-center justify-center">
                        <TrendingUpIcon className="w-4 h-4 text-green-400" />
                    </div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-green-400 font-bold uppercase mb-0.5">Activité du Jour</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[280px]">
                        <span className="font-bold text-white">{count}</span> consultations réalisées aujourd'hui.
                    </p>
                </div>
            </div>
        );
    };

    const renderNightPromo = () => {
        return (
            <div className="flex items-center gap-3 cursor-pointer" onClick={onOpenPremium}>
                <div className="relative shrink-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gold-600 to-yellow-500 flex items-center justify-center shadow-lg animate-pulse">
                        <StarIcon className="w-4 h-4 text-black fill-current" />
                    </div>
                </div>
                <div className="min-w-0">
                    <p className="text-[9px] text-gold-400 font-bold uppercase mb-0.5">Offre Spéciale Nuit</p>
                    <p className="text-white text-[10px] leading-tight truncate max-w-[280px]">
                        Réservez votre <span className="font-bold text-gold-400">Pack 50€</span> pour demain matin !
                    </p>
                </div>
            </div>
        );
    };

    // --- ROTATION CYCLE ---
    useEffect(() => {
        if (isNightMode) setCurrentActivity(renderNightStats());
        else setCurrentActivity(renderMediumLogin());

        const cycle = setInterval(() => {
            // SI PAUSE ACTIVE (Vrai avis affiché), ON NE CHANGE RIEN
            if (isPausedRef.current) return;

            setAnimationKey(prev => prev + 1);
            
            const rand = Math.random();

            if (isNightMode) {
                if (rand < 0.25) setCurrentActivity(renderNightWinner());
                else if (rand < 0.50) setCurrentActivity(renderTopRatedNight());
                else if (rand < 0.70) setCurrentActivity(renderNightPromo());
                else if (rand < 0.85) setCurrentActivity(renderNightWelcome());
                else setCurrentActivity(renderNightStats());
            } else {
                if (rand < 0.1) setCurrentActivity(renderWinner());
                else if (rand < 0.4) setCurrentActivity(renderConsultation());
                else if (rand < 0.7) setCurrentActivity(renderReview());
                else setCurrentActivity(renderMediumLogin());
            }

        }, 6000); 

        return () => clearInterval(cycle);
    }, [isNightMode]);

    const [isDismissed, setIsDismissed] = useState(false);

    if (isDismissed) return null;

    return (
        <div 
            className={`
                fixed z-30 bottom-20 left-3 md:bottom-4 md:left-4 max-w-[260px] sm:max-w-[280px]
                transition-all duration-500 transform ease-in-out
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none md:opacity-100 md:translate-y-0 md:pointer-events-auto'}
            `}
        >
            <div className={`backdrop-blur-xl border rounded-2xl shadow-[0_5px_25px_rgba(0,0,0,0.7)] overflow-hidden relative group ${isNightMode ? 'bg-black/90 border-indigo-900/50' : 'bg-mystic-900/95 border-gold-500/30'}`}>
                
                <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r animate-shimmer ${isNightMode ? 'from-indigo-900 via-purple-500 to-indigo-900' : 'from-purple-600 via-gold-500 to-purple-600'}`}></div>

                <div className="bg-black/40 px-3 py-1.5 flex items-center justify-between border-b border-white/5">
                    <div className="flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                          {!isNightMode ? (
                              <>
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
                              </>
                          ) : (
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-600"></span>
                          )}
                        </span>
                        <span className={`text-[9px] font-bold uppercase tracking-widest ${!isNightMode ? 'text-white' : 'text-gray-500'}`}>
                            {!isNightMode ? 'Live' : 'Offline'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isNightMode ? (
                            <div className="flex items-center gap-1">
                                <PhoneIcon className="w-2.5 h-2.5 text-green-400" />
                                <span className="text-[10px] font-mono font-bold text-green-400">
                                    {consultationCount}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1">
                                <MoonIcon className="w-2.5 h-2.5 text-indigo-400" />
                                <span className="text-[8px] text-gray-500 uppercase font-bold">Fermé</span>
                            </div>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); setIsDismissed(true); }} className="text-gray-500 hover:text-white p-0.5 transition-colors" title="Masquer"><XIcon className="w-3 h-3" /></button>
                    </div>
                </div>

                <div key={animationKey} className="p-2 md:p-3 animate-slide-in-right relative z-10">
                    {currentActivity}
                </div>

                <div className="bg-white/5 px-2 py-1 flex items-center justify-center gap-1.5">
                    <ShieldIcon className="w-2.5 h-2.5 text-gray-600" />
                    <span className="text-[8px] text-gray-600 uppercase tracking-wider">Anonyme & Sécurisé</span>
                </div>
            </div>

            <style>{`
                @keyframes shimmer {
                    0% { background-position: -200% 0; }
                    100% { background-position: 200% 0; }
                }
                .animate-shimmer {
                    background-size: 200% 100%;
                    animation: shimmer 3s infinite linear;
                }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(-5px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                .animate-slide-in-right {
                    animation: slideInRight 0.5s ease-out forwards;
                }
            `}</style>
        </div>
    );
};

export default LiveActivityFeed;
